/**
 * Dated edits (TIMELINE R24, decision 3) on the wire: every change to an item takes effect from a
 * day, today by default, and the item keeps its history, so the past is never rewritten.
 *
 * A transaction holds its latest values; `history` lists the changes, oldest first, each with the
 * fields as they were before its `from`. Seen day by day the item is a run of segments, and every
 * function here works on those: an edit from day D keeps the segments before D as they were and
 * takes the edited item's from D on.
 */
import type { Wire } from '@map-my-money/engine';
import type { WirePlan } from './run.ts';

export type DatedField = 'amount' | 'annualIncreasePercent' | 'interestRate' | 'employerMatch';
export const DATED_FIELDS: readonly DatedField[] = ['amount', 'annualIncreasePercent', 'interestRate', 'employerMatch'];

type Transaction = Wire.Transaction;
type Values = Readonly<Record<DatedField, string | null>>;

export interface Segment {
  /** The first day of the segment; null: from the item's start. */
  readonly from: string | null;
  readonly values: Values;
}

/** A debt's rate is dated by its rate schedule (`interestRateChanges`), not by its history. */
export const datedFieldsOf = (t: Pick<Transaction, 'type'>): readonly DatedField[] =>
  t.type === 'debt' ? DATED_FIELDS.filter((f) => f !== 'interestRate') : DATED_FIELDS;

const NOT_SET: Readonly<Record<DatedField, string | null>> = { amount: null, annualIncreasePercent: '0', interestRate: null, employerMatch: null };

const valuesOf = (t: Transaction): Values => ({
  amount: t.amount,
  annualIncreasePercent: t.annualIncreasePercent ?? NOT_SET.annualIncreasePercent,
  interestRate: t.interestRate ?? null,
  employerMatch: t.employerMatch ?? null,
});

const same = (a: string | null, b: string | null): boolean => (a === null || b === null ? a === b : Number(a) === Number(b));

const sameValues = (a: Values, b: Values, fields: readonly DatedField[] = DATED_FIELDS): boolean => fields.every((f) => same(a[f], b[f]));

/** The item's segments, oldest first. */
export function segmentsOf(t: Transaction): Segment[] {
  const history = [...(t.history ?? [])].sort((a, b) => a.from.localeCompare(b.from));
  const segments: Segment[] = [];
  let values = valuesOf(t);
  for (let i = history.length - 1; i >= 0; i--) {
    const change = history[i]!;
    segments.unshift({ from: change.from, values });
    values = { ...values, ...(change.was as Partial<Values>) };
  }
  segments.unshift({ from: null, values });
  return segments;
}

/** The dated values in force on `day`. */
export function valuesOn(t: Transaction, day: string): Values {
  const segments = segmentsOf(t);
  let found = segments[0]!.values;
  for (const s of segments) if (s.from === null || s.from <= day) found = s.values;
  return found;
}

/** The item as it was on `day`: its latest fields with the dated ones in force that day, and no history. */
export function itemOn(t: Transaction, day: string): Transaction {
  const values = valuesOn(t, day);
  const { history: _, ...rest } = t;
  const out: Record<string, unknown> = { ...rest };
  for (const f of DATED_FIELDS) {
    const v = values[f];
    if (v === null || (f === 'annualIncreasePercent' && t.annualIncreasePercent === undefined && Number(v) === 0)) delete out[f];
    else out[f] = v;
  }
  return out as Transaction;
}

/**
 * The item with `segments` as its history: its latest values are the last segment's, each change
 * says only what differed before it, and segments that change nothing, or end by the item's start,
 * are dropped.
 */
function withSegments(t: Transaction, segments: readonly Segment[]): Transaction {
  const live = segments.filter((s, i) => {
    const next = segments[i + 1];
    return next === undefined || next.from === null || next.from > t.startDate;
  });
  const merged: Segment[] = [];
  for (const s of live) {
    const previous = merged[merged.length - 1];
    if (previous && sameValues(previous.values, s.values)) continue;
    merged.push(merged.length === 0 ? { from: null, values: s.values } : s);
  }
  const latest = merged[merged.length - 1]!.values;
  const history: Wire.DatedChange[] = merged.slice(1).map((s, i) => {
    const before = merged[i]!.values;
    const was = Object.fromEntries(DATED_FIELDS.filter((f) => !same(before[f], s.values[f])).map((f) => [f, before[f]]));
    return { from: s.from!, was };
  });
  const { history: _, ...rest } = t;
  const out: Record<string, unknown> = { ...rest };
  for (const f of DATED_FIELDS) {
    const v = latest[f];
    if (v === null) delete out[f];
    else if (f === 'annualIncreasePercent' && t.annualIncreasePercent === undefined && Number(v) === 0) delete out[f];
    else out[f] = v;
  }
  if (history.length > 0) out.history = history;
  return out as Transaction;
}

/** The item with its history tidied: sorted, no empty or duplicate changes, none on or before its start. */
export const normalised = (t: Transaction): Transaction => withSegments(t, segmentsOf(t));

const rateScheduleFrom = (stored: Transaction, edited: Transaction, from: string): Transaction => {
  if (edited.type !== 'debt' || same(stored.interestRate ?? null, edited.interestRate ?? null) || edited.interestRate === undefined || stored.interestRate === undefined) return edited;
  const changes = (edited.interestRateChanges ?? []).filter((c) => c.date !== from);
  return {
    ...edited,
    interestRate: stored.interestRate,
    interestRateChanges: [...changes, { date: from, newRate: edited.interestRate }].sort((a, b) => a.date.localeCompare(b.date)),
  };
};

/**
 * `edited` taking effect from `from` (decision 3): before `from` the dated fields keep `stored`'s
 * values, from `from` on they follow `edited` (its own later changes included); everything else
 * (label, frequency, dates) is `edited`'s. A debt's new rate joins its rate schedule on `from`.
 * From the item's start or earlier, it is a correction: `edited` as it is ("it was always so").
 */
export function changedFrom(stored: Transaction, edited: Transaction, from: string): Transaction {
  if (from <= edited.startDate) return normalised(edited);
  const next = rateScheduleFrom(stored, edited, from);
  const fields = datedFieldsOf(next);
  const before = segmentsOf(stored).filter((s) => s.from === null || s.from < from);
  const after = segmentsOf(next);
  const kept = before.map((s) => ({ from: s.from, values: { ...valuesAt(after, s.from), ...pick(s.values, fields) } }));
  const fromOn = [{ from, values: valuesAt(after, from) }, ...after.filter((s) => s.from !== null && s.from > from)];
  return withSegments(next, [...kept, ...fromOn]);
}

const pick = (values: Values, fields: readonly DatedField[]): Partial<Values> => Object.fromEntries(fields.map((f) => [f, values[f]]));

const valuesAt = (segments: readonly Segment[], day: string | null): Values => {
  let found = segments[0]!.values;
  if (day === null) return found;
  for (const s of segments) if (s.from === null || s.from <= day) found = s.values;
  return found;
};

/** Whether `edited` says anything different from `stored` about the days before `from`. */
function rewritesBefore(stored: Transaction, edited: Transaction, from: string): boolean {
  const fields = datedFieldsOf(edited);
  const days = new Set<string>([edited.startDate, ...[...(stored.history ?? []), ...(edited.history ?? [])].map((c) => c.from)]);
  if ([...days].some((d) => d < from && !sameValues(valuesOn(stored, d), valuesOn(edited, d), fields))) return true;
  return edited.type === 'debt' && edited.startDate < from && !same(stored.interestRate ?? null, edited.interestRate ?? null);
}

/**
 * Every write's rule (REST plan saves, Claude's tools, the web before it saves): a transaction whose
 * dated fields change takes the change from the day `changesFrom` names for it, else from `today`,
 * and keeps what it was before that day. A new item, or one whose days before its edit are unchanged
 * (a future change, a removed future change, a later start), is kept as written.
 */
export function withDatedEdits<P extends WirePlan>(plan: P, previous: WirePlan | null, today: string, changesFrom: Readonly<Record<string, string>> = {}): P {
  if (previous === null) return plan;
  const stored = new Map((previous.transactions ?? []).map((t) => [t.id, t]));
  let changed = false;
  const transactions = plan.transactions?.map((t) => {
    const was = stored.get(t.id);
    const from = changesFrom[t.id] ?? today;
    if (!was || was.type !== t.type || !rewritesBefore(was, t, from)) return t;
    changed = true;
    return changedFrom(was, t, from);
  });
  return changed ? { ...plan, transactions } : plan;
}

/** The change starting on `from` taken out: the values before it carry on (a future change removed). */
export function withoutChange(t: Transaction, from: string): Transaction {
  const segments = segmentsOf(t);
  const index = segments.findIndex((s) => s.from === from);
  if (index <= 0) return t;
  const kept = segments.filter((_, i) => i !== index);
  return withSegments(t, kept);
}

export interface HistoryLine {
  /** First day of this stretch; null: since the item's start. */
  readonly from: string | null;
  /** Last day of this stretch; null: still in force, or for good once it starts. */
  readonly until: string | null;
  readonly values: Values;
  /** Which fields differ from the stretch before. */
  readonly changed: readonly DatedField[];
  readonly when: 'past' | 'current' | 'future';
}

const dayBefore = (day: string): string => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

/** The item's stretches for an editor: "£1,200 until 31 Oct 2026", "£1,400 from 1 Nov 2026". */
export function historyLines(t: Transaction, today: string): HistoryLine[] {
  const segments = segmentsOf(t);
  return segments.map((s, i) => {
    const next = segments[i + 1];
    const until = next?.from ? dayBefore(next.from) : null;
    const started = s.from === null || s.from <= today;
    const ended = until !== null && until < today;
    const previous = segments[i - 1];
    return {
      from: s.from,
      until,
      values: s.values,
      changed: previous ? DATED_FIELDS.filter((f) => !same(previous.values[f], s.values[f])) : [],
      when: ended ? 'past' : started ? 'current' : 'future',
    };
  });
}
